import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { ErrorBanner } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { ProjectDialog } from '@/components/tracking/project-dialog';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatAmount, formatDate } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import {
  PROJECT_STATUS_LABEL,
  useProjects,
  useTrackingMutation,
  useTrackingSettings,
  type Project,
  type ProjectStatus,
} from '@/lib/tracking';

export const Route = createFileRoute('/projects/')({ component: Projects });

const CHIPS: Array<{ key: ProjectStatus | 'all'; label: string }> = [
  { key: 'in_progress', label: 'In progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all', label: 'All' },
];

/** Screen PR1 (document 06): projects with income, costs and profit from the ledger. */
function Projects() {
  const orgId = useOrgId();
  const me = useMe();
  const settings = useTrackingSettings();
  const isStaff = me.data?.isStaff ?? false;
  const [status, setStatus] = useState<ProjectStatus | 'all'>('in_progress');
  const [q, setQ] = useState('');
  const list = useProjects({ status });
  const [editing, setEditing] = useState<Project | 'new' | null>(null);
  const act = useTrackingMutation(
    (o, v: { p: Project; status?: ProjectStatus; remove?: boolean }) =>
      v.remove
        ? api<unknown>(`/orgs/${o}/projects/${v.p.id}`, { method: 'DELETE' })
        : api<unknown>(`/orgs/${o}/projects/${v.p.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: v.status }),
          }),
  );
  if (!orgId) return <EmptyState title="Select a client" />;
  const f = q.trim().toLowerCase();
  const rows = (list.data ?? []).filter(
    (p) => !f || `${p.name} ${p.customerName}`.toLowerCase().includes(f),
  );
  return (
    <div>
      <PageHeader
        title="Projects"
        description="Income and costs of each piece of work for a customer, straight from the books."
        actions={isStaff ? <Button onClick={() => setEditing('new')}>New project</Button> : null}
      />
      {!settings.projects ? (
        <p className="mb-3 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          Projects are off, so forms do not show them. Switch them on in Company settings › Advanced
          › Projects.
        </p>
      ) : null}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {CHIPS.map((c) => (
          <Button
            key={c.key}
            size="sm"
            variant={status === c.key ? 'default' : 'outline'}
            onClick={() => setStatus(c.key)}
          >
            {c.label}
          </Button>
        ))}
        <Input
          aria-label="Search projects"
          placeholder="Search by project or customer"
          className="ml-auto w-72"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <ErrorBanner>{act.error?.message ?? null}</ErrorBanner>
      {list.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : list.isError ? (
        <EmptyState title="Could not load projects" hint={list.error.message} />
      ) : !rows.length ? (
        <EmptyState
          title="No projects here"
          hint="A project tracks income and costs for one customer."
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Start date</TableHead>
                <TableHead>End date</TableHead>
                <TableHead className="text-right">Income</TableHead>
                <TableHead className="text-right">Costs</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead className="text-right">Open balance</TableHead>
                <TableHead className="w-[140px]">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`project-${p.name}`}>
                  <TableCell>
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: p.id }}
                      className="font-medium hover:text-primary hover:underline"
                    >
                      {p.name}
                    </Link>
                  </TableCell>
                  <TableCell>{p.customerName}</TableCell>
                  <TableCell>{PROJECT_STATUS_LABEL[p.status]}</TableCell>
                  <TableCell>{p.startDate ? formatDate(p.startDate) : ''}</TableCell>
                  <TableCell>{p.endDate ? formatDate(p.endDate) : ''}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(p.income)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(p.costs)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(p.profit)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(p.openBalance)}</TableCell>
                  <TableCell>
                    {isStaff ? (
                      <div className="flex items-center">
                        <Button
                          variant="link"
                          size="sm"
                          className="px-1"
                          onClick={() => setEditing(p)}
                        >
                          Edit
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`More actions for ${p.name}`}
                            >
                              <ChevronDown className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {(['in_progress', 'completed', 'cancelled'] as const)
                              .filter((s) => s !== p.status)
                              .map((s) => (
                                <DropdownMenuItem
                                  key={s}
                                  onSelect={() => act.mutate({ p, status: s })}
                                >
                                  {s === 'in_progress'
                                    ? 'Reopen'
                                    : `Mark ${PROJECT_STATUS_LABEL[s].toLowerCase()}`}
                                </DropdownMenuItem>
                              ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() =>
                                act.mutate(
                                  { p, remove: true },
                                  { onSuccess: () => toast(`Project "${p.name}" deleted.`) },
                                )
                              }
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ProjectDialog project={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
